import React from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from '@ionic/react';
import styled from 'styled-components';
import Form from '../../common/Form';
import SiteForm, { SiteFormProps } from './SiteForm';
import useIonicModalProps from '../../../hooks/useIonicModalProps';

interface SiteFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  SiteFormProps: SiteFormProps;
}

const SiteModal = styled(IonModal)`
  @media only screen and (min-width: 768px) and (min-height: 768px) {
    --height: 768px;
  }
`;

const StyledSiteForm = styled(Form)`
  padding: 16px;
`;

const SiteFormDialog: React.FC<SiteFormDialogProps> = ({
  isOpen,
  onClose,
  SiteFormProps,
}) => {
  const modalProps = useIonicModalProps();
  const [presentAlert] = useIonAlert();
  const [isDirty, setIsDirty] = React.useState(false);

  // Reset dirty state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsDirty(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isDirty) {
      presentAlert({
        header: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close?',
        buttons: [
          {
            text: 'Stay here',
            role: 'cancel',
          },
          {
            text: 'Close',
            role: 'destructive',
            handler: () => {
              onClose();
            },
          },
        ],
      });
    } else {
      onClose();
    }
  };

  const title = SiteFormProps.initialValue
    ? 'Edit Site Details'
    : 'Add Site Details';
  const saveText = SiteFormProps.initialValue ? 'Save' : 'Done';

  const FormComponent = React.useMemo(
    () =>
      ({ children }: { children?: React.ReactNode }) =>
        (
          <IonContent>
            <StyledSiteForm>{children}</StyledSiteForm>
          </IonContent>
        ),
    []
  );

  const renderSubmitButton = React.useMemo(
    () =>
      ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) =>
        (
          <IonFooter
            style={{
              padding: '16px 8px',
              paddingBottom: 'calc(16px + var(--ion-safe-area-bottom, 0px))',
            }}
          >
            <IonButton expand="full" onClick={onClick} disabled={disabled}>
              {saveText}
            </IonButton>
          </IonFooter>
        ),
    [saveText]
  );

  const handleFormChange = React.useCallback(
    (dirty: boolean) => setIsDirty(dirty),
    []
  );

  return (
    <SiteModal isOpen={isOpen} onDidDismiss={onClose} {...modalProps}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <SiteForm
        Form={FormComponent}
        renderSubmitButton={renderSubmitButton}
        onFormChange={handleFormChange}
        {...SiteFormProps}
      />
    </SiteModal>
  );
};

export default SiteFormDialog;
